import * as Api from '@ViewModels';
import { FilterOperator } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../../../hooks/useDebounceValue';
import { useGetCampaignByIdQuery } from '../../../../../../queries/CampaignReporting/useGetCampaignById';
import { TextInput } from '../../../../../components/TextInput';
import { styleSheet as simpleAutoCompleteStyleSheet } from '../../../../../components/autocomplete/SimpleAutoCompleteSearchField/styles';
import { Badge } from '../../../../../components/dataBoards/Badge';
import { DateRangeFilterDropdown } from '../../../../../components/dataBoards/donations/DateRangeFilterDropdown';
import { DonationCampaignSearch } from '../../../../../components/dataBoards/donations/DonationCampaignSearch';
import { DonationTagSearch } from '../../../../../components/dataBoards/donations/DonationTagSearch';
import { AmountFilterOption, DonationTypeOption } from '../../../../../components/dataBoards/donations/models';
import { SearchIcon } from '../../../../../components/svgs/icons/SearchIcon';
import { XIcon } from '../../../../../components/svgs/icons/XIcon';
import { white } from '../../../../../styles/colors';
import { baseStyleSheet } from '../../../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../../../../DataBoards/styles';
import { styleSheet } from './styles';

interface DonationsFiltersProps {
	filterCriteria: Api.IDonationFilterCriteria[];
	onFilterChange: (criteria: Api.IDonationFilterCriteria[]) => void;
}

export const DonationsFilters: React.FC<DonationsFiltersProps> = ({ filterCriteria, onFilterChange }) => {
	// Donor Name - always active
	const [donorNameSearch, setDonorNameSearch] = React.useState('');
	const debouncedDonorNameSearch = useDebounceValue(donorNameSearch, 500);

	// Campaign - always visible
	const [selectedCampaign, setSelectedCampaign] = React.useState<Api.IDonationCampaign>(null);

	// Tags - always visible
	const [selectedTag, setSelectedTag] = React.useState<Api.IAccountTag>(null);

	// Amount filter
	const [selectedAmountFilter, setSelectedAmountFilter] = React.useState<AmountFilterOption>(
		AmountFilterOption.OneTime
	);
	const [minimumDonation, setMinimumDonation] = React.useState<string>('');
	const [maximumDonation, setMaximumDonation] = React.useState<string>('');

	// Date range filter
	const [selectedDonationType, setSelectedDonationType] = React.useState<DonationTypeOption>(
		DonationTypeOption.HasDonated
	);
	const [minimumDonationDate, setMinimumDonationDate] = React.useState<string>(null);
	const [maximumDonationDate, setMaximumDonationDate] = React.useState<string>(null);
	const [isFirstDonation, setIsFirstDonation] = React.useState<boolean>(false);

	// Normalize empty strings to null for date values
	const normalizedMinimumDonationDate = minimumDonationDate || null;
	const normalizedMaximumDonationDate = maximumDonationDate || null;

	const isFilteringNotDonated = selectedDonationType === DonationTypeOption.NotDonated;

	// Ref to prevent infinite loops when parsing from URL
	const isParsingFromUrlRef = React.useRef(false);
	// Initialize with empty string to ensure parsing runs on mount
	const previousCriteriaStringRef = React.useRef<string>('');
	// Ref to track clear operations for tag search remounting
	const tagSearchClearCounterRef = React.useRef(0);
	// Ref to track if this is the initial mount
	const isInitialMountRef = React.useRef(true);

	// Convert filter state to criteria
	const criteria = React.useMemo(() => {
		const result: Api.IDonationFilterCriteria[] = [];

		// Donor name filter
		if (debouncedDonorNameSearch) {
			result.push({
				property: Api.DonationFilterProperty.Name,
				value: debouncedDonorNameSearch,
			});
		}

		// Campaign filter
		if (selectedCampaign?.id) {
			result.push({
				property: Api.DonationFilterProperty.Campaign,
				value: selectedCampaign.id,
			});
		}

		// Tag filter
		if (selectedTag?.name) {
			result.push({
				property: Api.DonationFilterProperty.Tag,
				value: selectedTag.name,
			});
		}

		// Amount filter
		if (minimumDonation || maximumDonation) {
			const isLifetime = selectedAmountFilter === AmountFilterOption.Lifetime;
			const amountProperty = isLifetime ? (Api.DonorFilterProperty.Lifetime as any) : Api.DonationFilterProperty.Amount;

			if (minimumDonation) {
				result.push({
					property: amountProperty,
					op: FilterOperator.Gt,
					value: minimumDonation,
				} as Api.IDonationFilterCriteria);
			}
			if (maximumDonation) {
				result.push({
					property: amountProperty,
					op: FilterOperator.Lt,
					value: maximumDonation,
				} as Api.IDonationFilterCriteria);
			}
		}

		// Date filter
		if (normalizedMinimumDonationDate) {
			if (selectedDonationType === DonationTypeOption.HasDonated) {
				// "Has donated" with date range
				result.push({
					property: Api.DonationFilterProperty.Date,
					op: FilterOperator.Gt,
					value: normalizedMinimumDonationDate,
				});
				if (normalizedMaximumDonationDate) {
					result.push({
						property: Api.DonationFilterProperty.Date,
						op: FilterOperator.Lt,
						value: normalizedMaximumDonationDate,
					});
				}
			} else if (selectedDonationType === DonationTypeOption.NotDonated) {
				// "Not donated" since date
				result.push({
					property: Api.DonorFilterProperty.LastDonationDate as any,
					op: FilterOperator.Lt,
					value: normalizedMinimumDonationDate,
				} as Api.IDonationFilterCriteria);
			}
		}

		// FirstTimeDonor filter
		if (isFirstDonation && selectedDonationType === DonationTypeOption.HasDonated) {
			result.push({
				property: Api.DonationFilterProperty.FirstTimeDonor,
				value: 'true',
			});
		}

		return result;
	}, [
		debouncedDonorNameSearch,
		selectedCampaign,
		selectedTag,
		minimumDonation,
		maximumDonation,
		selectedAmountFilter,
		normalizedMinimumDonationDate,
		normalizedMaximumDonationDate,
		selectedDonationType,
		isFirstDonation,
	]);

	// Notify parent when criteria changes (but not when parsing from URL)
	React.useEffect(() => {
		if (isParsingFromUrlRef.current) {
			return;
		}
		// Skip on initial mount if we have URL criteria to parse (to avoid clearing URL filters)
		if (isInitialMountRef.current && filterCriteria && filterCriteria.length > 0) {
			return;
		}
		const criteriaString = JSON.stringify(criteria);
		if (criteriaString !== previousCriteriaStringRef.current) {
			previousCriteriaStringRef.current = criteriaString;
			onFilterChange(criteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [criteria]);

	// Parse URL criteria and initialize filter state
	React.useEffect(() => {
		const criteriaString = JSON.stringify(filterCriteria || []);
		const isInitialMount = isInitialMountRef.current;

		// On initial mount, always parse. Otherwise, only parse if criteria changed.
		if (!isInitialMount && criteriaString === previousCriteriaStringRef.current) {
			return; // No change, skip parsing
		}

		previousCriteriaStringRef.current = criteriaString;
		isParsingFromUrlRef.current = true;

		// Mark that initial mount is complete after first parse
		if (isInitialMount) {
			isInitialMountRef.current = false;
		}
		if (!filterCriteria || filterCriteria.length === 0) {
			// Reset all filters if no criteria
			// Increment clear counter to force tag search remount when clearing
			if (selectedTag) {
				tagSearchClearCounterRef.current += 1;
			}
			setDonorNameSearch('');
			setSelectedCampaign(null);
			setSelectedTag(null);
			setMinimumDonation('');
			setMaximumDonation('');
			setMinimumDonationDate(null);
			setMaximumDonationDate(null);
			setSelectedDonationType(DonationTypeOption.HasDonated);
			setIsFirstDonation(false);
			setSelectedAmountFilter(AmountFilterOption.OneTime);
			// Reset the flag after state updates
			setTimeout(() => {
				isParsingFromUrlRef.current = false;
			}, 0);
			return;
		}

		// Parse criteria to initialize filter state
		let parsedDonorName = '';
		let parsedCampaign: Api.IDonationCampaign = null;
		let parsedTag: Api.IAccountTag = null;
		let parsedAmountFilter = AmountFilterOption.OneTime;
		let parsedMinDonation = '';
		let parsedMaxDonation = '';
		let parsedDonationType = DonationTypeOption.HasDonated;
		let parsedMinDate: string = null;
		let parsedMaxDate: string = null;
		let parsedIsFirstDonation = false;

		// Helper to recursively parse nested criteria
		const parseCriteriaRecursive = (criteriaList: Api.IDonationFilterCriteria[]) => {
			for (const crit of criteriaList) {
				if (crit.criteria && crit.criteria.length > 0) {
					parseCriteriaRecursive(crit.criteria);
				} else if (crit.property) {
					const property = crit.property as string;
					switch (property) {
						case Api.DonationFilterProperty.Name:
						case 'Name':
							if (crit.value) {
								parsedDonorName = crit.value;
							}
							break;
						case Api.DonationFilterProperty.Campaign:
						case 'Campaign':
							if (crit.value) {
								parsedCampaign = { id: crit.value } as Api.IDonationCampaign;
							}
							break;
						case Api.DonationFilterProperty.Tag:
						case 'Tag':
							if (crit.value) {
								parsedTag = { name: crit.value } as Api.IAccountTag;
							}
							break;
						case Api.DonationFilterProperty.Amount:
						case 'Amount':
							parsedAmountFilter = AmountFilterOption.OneTime;
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinDonation = crit.value;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxDonation = crit.value;
							}
							break;
						case Api.DonorFilterProperty.Lifetime:
						case 'Lifetime':
							parsedAmountFilter = AmountFilterOption.Lifetime;
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinDonation = crit.value;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxDonation = crit.value;
							}
							break;
						case Api.DonationFilterProperty.Date:
						case 'Date':
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinDate = crit.value;
								parsedDonationType = DonationTypeOption.HasDonated;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxDate = crit.value;
								parsedDonationType = DonationTypeOption.HasDonated;
							}
							break;
						case Api.DonorFilterProperty.LastDonationDate:
						case 'LastDonationDate':
							if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMinDate = crit.value;
								parsedDonationType = DonationTypeOption.NotDonated;
							}
							break;
						case Api.DonationFilterProperty.FirstTimeDonor:
						case 'FirstTimeDonor':
							if (crit.value === 'true') {
								parsedIsFirstDonation = true;
							}
							break;
						default:
							break;
					}
				}
			}
		};

		parseCriteriaRecursive(filterCriteria);

		// Update state only if values changed to avoid infinite loops
		if (parsedDonorName !== donorNameSearch) {
			setDonorNameSearch(parsedDonorName);
		}
		if (parsedCampaign?.id !== selectedCampaign?.id) {
			setSelectedCampaign(parsedCampaign);
		}
		if (parsedTag?.name !== selectedTag?.name) {
			setSelectedTag(parsedTag);
		}
		if (parsedAmountFilter !== selectedAmountFilter) {
			setSelectedAmountFilter(parsedAmountFilter);
		}
		if (parsedMinDonation !== minimumDonation) {
			setMinimumDonation(parsedMinDonation);
		}
		if (parsedMaxDonation !== maximumDonation) {
			setMaximumDonation(parsedMaxDonation);
		}
		if (parsedDonationType !== selectedDonationType) {
			setSelectedDonationType(parsedDonationType);
		}
		if (parsedMinDate !== minimumDonationDate) {
			setMinimumDonationDate(parsedMinDate);
		}
		if (parsedMaxDate !== maximumDonationDate) {
			setMaximumDonationDate(parsedMaxDate);
		}
		if (parsedIsFirstDonation !== isFirstDonation) {
			setIsFirstDonation(parsedIsFirstDonation);
		}

		// Reset the flag after state updates
		setTimeout(() => {
			isParsingFromUrlRef.current = false;
		}, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterCriteria]);

	const _amountFilterDropdownText = React.useMemo(() => {
		if (!!selectedAmountFilter && !minimumDonation && !maximumDonation) {
			return 'Amount';
		}
		const duration = selectedAmountFilter === AmountFilterOption.OneTime ? '1-Time' : 'Lifetime';

		return `$${minimumDonation ?? '0'}${maximumDonation ? '-' + maximumDonation : '+'}/${duration}`;
	}, [selectedAmountFilter, minimumDonation, maximumDonation]);

	const hasActiveFilters = React.useMemo(() => {
		return (
			!!donorNameSearch ||
			!!selectedCampaign ||
			!!selectedTag ||
			!!minimumDonation ||
			!!maximumDonation ||
			!!minimumDonationDate ||
			!!maximumDonationDate ||
			!!isFirstDonation
		);
	}, [
		donorNameSearch,
		selectedCampaign,
		selectedTag,
		minimumDonation,
		maximumDonation,
		minimumDonationDate,
		maximumDonationDate,
		isFirstDonation,
	]);

	const clearFilters = React.useCallback(() => {
		// Set parsing flag to prevent the first useEffect from interfering
		isParsingFromUrlRef.current = true;
		// Reset the ref to ensure the parsing effect sees a change when filterCriteria updates
		previousCriteriaStringRef.current = '';
		// Increment clear counter to force tag search remount
		tagSearchClearCounterRef.current += 1;
		// Notify parent immediately that filters are cleared
		onFilterChange([]);
		// Reset all local state
		setDonorNameSearch('');
		setSelectedCampaign(null);
		setSelectedTag(null);
		setMinimumDonation('');
		setMaximumDonation('');
		setMinimumDonationDate(null);
		setMaximumDonationDate(null);
		setSelectedDonationType(DonationTypeOption.HasDonated);
		setIsFirstDonation(false);
		setSelectedAmountFilter(AmountFilterOption.OneTime);
		// Reset the flag after state updates complete
		setTimeout(() => {
			isParsingFromUrlRef.current = false;
		}, 0);
	}, [onFilterChange]);

	const onDonorNameInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setDonorNameSearch(e.target.value);
	}, []);

	const onCampaignSearchClear = React.useCallback(() => {
		setSelectedCampaign(null);
	}, []);

	const selectDonationCampaign = React.useCallback((donationCampaign: Api.IDonationCampaign) => {
		setSelectedCampaign(donationCampaign);
	}, []);

	// Fetch campaign details by ID to display the name
	const campaignDetailsQuery = useGetCampaignByIdQuery({
		id: selectedCampaign?.id || '',
		enabled: !!selectedCampaign?.id,
	});

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.filtersRow)}>
				{/* Donor Name Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<TextInput
						inputId='donor-name-filter-input'
						type='text'
						className={css(styleSheet.comboBoxRightAutocompleteResponsive)}
						inputClassName={css(dbStyleSheet.wideInputResponsive)}
						onChange={onDonorNameInputChanged}
						value={donorNameSearch}
						leftAccessory={<SearchIcon className={css(dbStyleSheet.searchIcon)} />}
						placeholder='Donor Name'
					/>
				</div>

				{/* Campaign Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<div>
						<DonationCampaignSearch
							placeholder='Campaign'
							className={css(dbStyleSheet.wideInput, dbStyleSheet.searchFieldReset)}
							onSelectDonationCampaign={selectDonationCampaign}
							dropdownClassName={css(dbStyleSheet.comboBoxDropdown)}
							canAdd={false}
							onClear={onCampaignSearchClear}
							initialSearchFragment={campaignDetailsQuery.data?.name || selectedCampaign?.name || ''}
						/>
						{campaignDetailsQuery.data?.name && (
							<div className={css(styleSheet.campaignBadgeWrapper)}>
								<div className={css(styleSheet.campaignBadge)}>
									<Badge
										text={campaignDetailsQuery.data.name}
										foregroundColor={campaignDetailsQuery.data.foregroundColor}
									/>
								</div>
								<button
									className={`${css(styleSheet.campaignBadgeRemove)} campaign-badge-remove`}
									onClick={onCampaignSearchClear}
									type='button'
									aria-label={`Remove ${campaignDetailsQuery.data.name}`}
								>
									<XIcon width={10} height={10} color={white} />
								</button>
							</div>
						)}
					</div>
				</div>

				{/* Tags Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<DonationTagSearch
						key={`donation-tag-search-${tagSearchClearCounterRef.current}`}
						inputStyle={[
							simpleAutoCompleteStyleSheet.searchFieldWhiteBackground,
							dbStyleSheet.comboBoxRightAutocompleteResponsive,
							dbStyleSheet.wideInputResponsive,
						]}
						placeholder='Tags'
						onTagSelected={setSelectedTag}
						onClear={() => setSelectedTag(null)}
						selectedTag={selectedTag}
					/>
				</div>

				{/* Amount Filter */}
				<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
					{/* <AmountFilterDropdown
						key={`amountFilterDropdown-${selectedAmountFilter}-${minimumDonation}-${maximumDonation}`}
						label={amountFilterDropdownText}
						initialAmountFilterOption={selectedAmountFilter}
						initialMinimumDonation={minimumDonation}
						initialMaximumDonation={maximumDonation}
						anchorClassName={css(dbStyleSheet.dropdownReset)}
						onChange={value => {
							setSelectedAmountFilter(value.amountFilterOption);
							setMinimumDonation(value.minimumDonation);
							setMaximumDonation(value.maximumDonation);
						}}
					/>*/}
				</div>

				{/* Date Range Filter */}
				<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
					<DateRangeFilterDropdown
						minimumDonationDate={minimumDonationDate}
						setMinimumDonationDate={setMinimumDonationDate}
						maximumDonationDate={maximumDonationDate}
						setMaximumDonationDate={setMaximumDonationDate}
						selectedDonationType={selectedDonationType}
						setSelectedDonationType={setSelectedDonationType}
						isFirstDonation={isFirstDonation}
						setIsFirstDonation={setIsFirstDonation}
						showCustomRange={!isFilteringNotDonated}
						anchorClassName={css(dbStyleSheet.dropdownReset)}
					/>
				</div>

				{/* Clear Filters Button */}
				{hasActiveFilters && (
					<div className={css(styleSheet.clearFiltersContainer)}>
						<button
							className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.clearSearchButton)}
							onClick={clearFilters}
						>
							<span>Clear Filters</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
